import { useThree, useFrame } from "@react-three/fiber";
import { useRef, useState, useEffect } from "react";
import * as THREE from "three";
import { log, error, warn, info, debug } from '../../../utils/consoleLogger';

export const useShowCorrectCoordinates = (
  showLabels,
  center,
  { coordinateData },
  controls
) => {
  // Store all 4 dimension sets in refs (no re-renders)
  const allDimensionSetsRef = useRef({
    frontRight: null,
    frontLeft: null,
    backRight: null,
    backLeft: null
  });

  // Store current active set data and name in refs for direct access
  const activeSetDataRef = useRef(null);
  const activeSetNameRef = useRef(null);

  const previousIsFrontRef = useRef(null);
  const previousIsRightRef = useRef(null);
  const previousDimensionsRef = useRef(null);
  const previousShowLabelsRef = useRef(false);
  // State to track when activeSetDataRef is ready - triggers re-render in parent
  const [isActiveSetReady, setIsActiveSetReady] = useState(false);
  const { camera } = useThree();

  // Only log when values are actually set (not during re-renders with undefined values)
  useEffect(() => {
    if (showLabels !== undefined && center !== undefined && coordinateData !== null) {
      log('show labels, center, coordinateData:', showLabels, center, coordinateData);
    }
  }, [showLabels, center, coordinateData]);

  const parseVector = (str) => {
    const [x, y, z] = str.split(",").map(Number);
    return new THREE.Vector3(x, y, z);
  };

  // Pre-compute all 4 dimension sets when coordinateData changes
  useEffect(() => {
    if (!coordinateData || !coordinateData.positions) return;
    if (coordinateData.positions.fixed) return;

    const createDimensionSet = (positions, labels) => {
      return positions.map((arrow, index) => {
        const result = {
          start: arrow.start,
          end: arrow.end,
          label: labels[index]?.label,
          labelText: labels[index]?.labelText || "Missing Dimension Label",
          offsetDirection: labels[index]?.offsetDirection?.clone(),
          scaleFactor: coordinateData.scaleFactor,
          isRotational: arrow.isRotational,
        };
        if (arrow.offsetstart !== undefined && arrow.offsetend !== undefined) {
          result.offsetstart = arrow.offsetstart;
          result.offsetend = arrow.offsetend;
        }
        return result;
      });
    };

    // Pre-compute all 4 sets
    const frontRight = createDimensionSet(
      coordinateData.positions,
      coordinateData.positionLabels
    );
    const frontLeft = createDimensionSet(
      coordinateData.positionsXInverted,
      coordinateData.positionLabelsXInverted
    );
    const backRight = createDimensionSet(
      coordinateData.positionsZInverted,
      coordinateData.positionLabelsZInverted
    );
    const backLeft = createDimensionSet(
      coordinateData.positionsXInvertedZInverted,
      coordinateData.positionLabelsXInvertedZInverted
    );

    allDimensionSetsRef.current = {
      frontRight,
      frontLeft,
      backRight,
      backLeft
    };

    previousDimensionsRef.current = coordinateData.positions;
  }, [coordinateData]);

  // Initialize activeSetDataRef immediately when showLabels becomes true
  // This ensures dimension lines render on first render instead of waiting for useFrame
  useEffect(() => {
    // Track transition from false to true
    const wasFalseNowTrue = !previousShowLabelsRef.current && showLabels;
    previousShowLabelsRef.current = showLabels;

    // Clear refs when showLabels becomes false
    if (!showLabels) {
      if (activeSetDataRef.current) {
        activeSetDataRef.current = null;
        activeSetNameRef.current = null;
        previousIsFrontRef.current = null;
        previousIsRightRef.current = null;
        setIsActiveSetReady(false);
      }
      return;
    }

    // Only proceed if showLabels is true and we have the required data
    if (!coordinateData || !center) return;
    if (!allDimensionSetsRef.current.frontRight) return; // Dimension sets not ready yet
    
    // Only initialize when transitioning to true or when not yet initialized
    if (!wasFalseNowTrue && activeSetDataRef.current) {
      // Already initialized and no transition, let useFrame handle updates
      return;
    }

    // Calculate current camera position relative to center
    const isFront = camera.position.z > center.z;
    const isRight = camera.position.x > center.x;

    // Determine which set should be visible (same logic as in useFrame)
    let activeSet;
    if (!isRight && isFront) {
      activeSet = 'frontRight';
    } else if (isRight && isFront) {
      activeSet = 'frontLeft';
    } else if (!isRight && !isFront) {
      activeSet = 'backRight';
    } else {
      activeSet = 'backLeft';
    }

    // Initialize activeSetDataRef and activeSetNameRef with the correct set
    activeSetNameRef.current = activeSet;
    activeSetDataRef.current = allDimensionSetsRef.current[activeSet];

    // Initialize previous refs to prevent immediate update in useFrame
    previousIsFrontRef.current = isFront;
    previousIsRightRef.current = isRight;
    
    // Trigger re-render in parent component
    setIsActiveSetReady(true);
  }, [showLabels, coordinateData, center]);

  // Update activeSet synchronously in useFrame (like ModelViewerOldDim.js)
  // This avoids the 150ms lag from asynchronous OrbitControls 'change' events
  useFrame(() => {
    if (!showLabels || !coordinateData || !center) return;

    const { positions } = coordinateData;
    const isFront = camera.position.z > center.z;
    const isRight = camera.position.x > center.x;

    // Check if isFront or isRight has changed (threshold crossed)
    const hasIsFrontOrIsRightChanged = 
       isFront !== previousIsFrontRef.current ||
       isRight !== previousIsRightRef.current;

    const hasDimensionsChanged = previousDimensionsRef.current !== positions;

    // Only update if something changed
    if (hasIsFrontOrIsRightChanged || hasDimensionsChanged) {
      previousIsFrontRef.current = isFront;
      previousIsRightRef.current = isRight;
      previousDimensionsRef.current = positions;

      // Determine which set should be visible
      let activeSet;
      if (!isRight && isFront) {
        activeSet = 'frontRight';
      } else if (isRight && isFront) {
        activeSet = 'frontLeft';
      } else if (!isRight && !isFront) {
        activeSet = 'backRight';
      } else {
        activeSet = 'backLeft';
      }

      log("isRight, isFront, activeSet:", isRight, isFront, activeSet);

      // Update refs directly ONLY (no state update = no re-render)
      // This prevents React from scheduling re-renders in useFrame
      if (activeSetNameRef.current !== activeSet) {
        activeSetNameRef.current = activeSet;
        activeSetDataRef.current = allDimensionSetsRef.current[activeSet];
        // Only update state if it was false (initialization case)
        // For subsequent updates in useFrame, we rely on the components updating via refs
        if (!isActiveSetReady) {
          setIsActiveSetReady(true);
        }
      }
    }
  });

  // Create stable return object - initialize with default values
  const returnValueRef = useRef({
    allDimensionSets: {
      frontRight: null,
      frontLeft: null,
      backRight: null,
      backLeft: null,
      activeSet: null
    },
    positionAndLabelOutput: null
  });

  // Initialize return value and activeSetDataRef when coordinateData is available
  useEffect(() => {
    if (allDimensionSetsRef.current.frontRight && !returnValueRef.current.allDimensionSets.frontRight) {
      // Initialize activeSetDataRef with first set (frontRight) if not already set
      if (!activeSetDataRef.current) {
        activeSetNameRef.current = 'frontRight';
        activeSetDataRef.current = allDimensionSetsRef.current.frontRight;
      }
      
      returnValueRef.current = {
        allDimensionSets: {
          frontRight: allDimensionSetsRef.current.frontRight,
          frontLeft: allDimensionSetsRef.current.frontLeft,
          backRight: allDimensionSetsRef.current.backRight,
          backLeft: allDimensionSetsRef.current.backLeft,
          activeSet: activeSetNameRef.current || 'frontRight'
        },
        positionAndLabelOutput: activeSetDataRef.current || allDimensionSetsRef.current.frontRight
      };
      log('returnValueRef, allDimensionSetsRef, activeSetDataRef, activeSetNameRef:', 
        returnValueRef.current, allDimensionSetsRef.current, activeSetDataRef.current, activeSetNameRef.current);
    }
  }, [coordinateData]);

  // Expose refs directly so parent can read them synchronously in useFrame
  // Also return current values for backward compatibility
  return {
    allDimensionSetsRef: allDimensionSetsRef,
    activeSetDataRef: activeSetDataRef,
    activeSetNameRef: activeSetNameRef,
    allDimensionSets: returnValueRef.current.allDimensionSets,
    positionAndLabelOutput: returnValueRef.current.positionAndLabelOutput,
    isActiveSetReady: isActiveSetReady
  };
};